import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  ErrorPage,
  PageContent,
  PageHeader,
  PageShell,
  PageSkeleton,
} from "@h2j-data-team/ui";

import type { QlikAuthMethod, UpdateQlikConnectionInput } from "@repo/shared";
import { toast } from "@/components/shared";
import { BackLink } from "@/components/back-link";
import { supportCode } from "@/components/error-screen";
import { connectionQueryOptions, useUpdateConnection } from "@/features/qlik-connections/api";
import { ConnectionFormFields } from "@/features/qlik-connections/components/connection-form";
import { ConnectionHelpAside } from "@/features/qlik-connections/components/connection-help-aside";
import {
  makeEditConnectionFormSchema,
  type EditConnectionFormData,
} from "@/features/qlik-connections/schemas";
import { ApiError, getErrorMessage } from "@/lib/api-client";

export const Route = createFileRoute("/_authenticated/connections_/$id/edit")({
  staticData: { pageTitle: "breadcrumbs.editConnection", breadcrumb: "breadcrumbs.editConnection" },
  component: EditConnectionPage,
});

function EditConnectionPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: connection, isLoading, error, refetch } = useQuery(connectionQueryOptions(id));
  const updateMutation = useUpdateConnection();
  const { t } = useTranslation("connections");
  const { t: tc } = useTranslation("common");
  const editConnectionFormSchema = useMemo(() => makeEditConnectionFormSchema(t), [t]);

  const currentMethod: QlikAuthMethod = connection?.authMethod ?? "api_key";
  const [authMethod, setAuthMethod] = useState<QlikAuthMethod>(currentMethod);

  useEffect(() => {
    setAuthMethod(currentMethod);
  }, [currentMethod]);

  const form = useForm<EditConnectionFormData>({
    resolver: zodResolver(editConnectionFormSchema),
    values: {
      name: connection?.name ?? "",
      baseUrl: connection?.baseUrl ?? "",
      apiKey: "",
      clientId: connection?.oauthClientId ?? "",
      clientSecret: "",
    },
  });

  const switching = authMethod !== currentMethod;

  const onSubmit = async (data: EditConnectionFormData) => {
    if (switching) {
      if (authMethod === "api_key") {
        if (!data.apiKey) {
          toast.error(t("edit.apiKeyRequired"));
          return;
        }
        await runUpdate({
          authMethod: "api_key",
          name: data.name,
          baseUrl: data.baseUrl,
          apiKey: data.apiKey,
        });
        return;
      }
      if (!data.clientId || !data.clientSecret) {
        toast.error(t("edit.oauthRequired"));
        return;
      }
      await runUpdate({
        authMethod: "oauth_client_credentials",
        name: data.name,
        baseUrl: data.baseUrl,
        clientId: data.clientId,
        clientSecret: data.clientSecret,
      });
      return;
    }

    const updateData: UpdateQlikConnectionInput =
      authMethod === "oauth_client_credentials"
        ? { authMethod: "oauth_client_credentials" }
        : { authMethod: "api_key" };

    if (data.name !== connection?.name) updateData.name = data.name;
    if (data.baseUrl !== connection?.baseUrl) updateData.baseUrl = data.baseUrl;

    if (authMethod === "api_key") {
      if (data.apiKey) (updateData as { apiKey?: string }).apiKey = data.apiKey;
    } else {
      const base = updateData as { clientId?: string; clientSecret?: string };
      if (data.clientId && data.clientId !== connection?.oauthClientId)
        base.clientId = data.clientId;
      if (data.clientSecret) base.clientSecret = data.clientSecret;
    }

    if (Object.keys(updateData).length === 1) {
      toast.info(t("edit.noChanges"));
      return;
    }

    await runUpdate(updateData);
  };

  async function runUpdate(payload: UpdateQlikConnectionInput) {
    try {
      await updateMutation.mutateAsync({ id, data: payload });
      toast.success(t("edit.updated"));
      navigate({ to: "/connections", search: { q: undefined } });
    } catch {
      // Error surfaced via updateMutation.error
    }
  }

  const back = (
    <BackLink to="/connections" search={{ q: undefined }} label={t("edit.backToConnections")} />
  );

  if (isLoading) {
    return (
      <PageShell header={<PageHeader title={t("edit.pageTitle")} back={back} />}>
        <PageContent>
          <PageSkeleton variant="form" label={t("edit.loading")} />
        </PageContent>
      </PageShell>
    );
  }

  if (error || !connection) {
    const notFound = !error || (error instanceof ApiError && error.status === 404);
    return (
      <ErrorPage
        kind={notFound ? "not-found" : "load-failed"}
        header={<PageHeader title={t("edit.pageTitle")} back={back} />}
        title={notFound ? t("edit.notFound.title") : t("edit.loadFailed")}
        description={notFound ? t("edit.notFound.description") : undefined}
        supportCode={supportCode(error, tc("error.supportCode"))}
        actions={
          notFound ? undefined : (
            <Button variant="outline" onClick={() => void refetch()}>
              {tc("tryAgain")}
            </Button>
          )
        }
      />
    );
  }

  return (
    <PageShell
      header={
        <PageHeader
          title={t("edit.pageTitle")}
          description={t("edit.description", { name: connection.name })}
          back={back}
        />
      }
    >
      <PageContent>
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-w-0">
            <ConnectionFormFields
              mode="edit"
              formId="edit-connection-form"
              authMethod={authMethod}
              onAuthMethodChange={setAuthMethod}
              register={form.register}
              errors={form.formState.errors}
              switching={switching}
              onSubmit={form.handleSubmit(onSubmit)}
              busy={updateMutation.isPending}
              submitLabel={t("edit.save")}
              submittingLabel={t("edit.saving")}
              cancel={
                <Button
                  variant="outline"
                  onClick={() => navigate({ to: "/connections", search: { q: undefined } })}
                >
                  {t("form.cancel")}
                </Button>
              }
              submitError={updateMutation.error ? getErrorMessage(updateMutation.error) : undefined}
            />
          </div>
          <ConnectionHelpAside authMethod={authMethod} />
        </div>
      </PageContent>
    </PageShell>
  );
}
